import { useRouter } from 'next/router'
import { useMemo } from 'react'
import { FaFacebook, FaLink } from 'react-icons/fa'

import { NotificationType, notify } from 'src/providers/NotificationProvider'

import { Button } from 'src/components/atoms/Button'
import { ButtonLink } from 'src/components/atoms/Link'

import { whyNativeTreesPath } from 'src/config/paths'
import { env } from 'src/env/client.mjs'

import InformationBlock, {
  InformationBlockImage,
  InformationBlockProps
} from '../pages/crowdfund/[uri]/InformationBlock'

export enum SocialBlockType {
  WhyNativeTrees = 'WhyNativeTrees',
  Share = 'Share'
}

const useSocialBlocks = () => {
  const router = useRouter()

  const { asPath: path } = router
  const pageUrl = useMemo(() => `${env.NEXT_PUBLIC_APP_DOMAIN}${path}`, [path])
  const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php/?u=${pageUrl}`
  const linkedInShareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${pageUrl}`

  const handleShareClick = (shareUrl: string) => {
    window.open(shareUrl, 'share-dialog', 'width=600,height=600')
  }

  const handleShareFacebook = () => handleShareClick(facebookShareUrl)

  const handleShareLinkedIn = () => handleShareClick(linkedInShareUrl)

  const handleCopyToClipboard = (url?: string) => {
    navigator.clipboard.writeText(url || pageUrl)
    notify({ type: NotificationType.info, message: 'Link copied to clipboard' })
  }

  const informationBlocks: { [key in SocialBlockType]: InformationBlockProps } =
    {
      [SocialBlockType.Share]: {
        heading: 'Share the love!',
        description:
          'We’re building a tree planting movement and we’d love your support – share this campaign to help spread the message!',
        image: InformationBlockImage.Business,
        buttons: (
          <>
            <Button
              className='group min-w-[128px]'
              color='light'
              variant='outlined'
              onClick={handleShareFacebook}
            >
              Share
              <FaFacebook className='mx-2 my-0 lg:my-2 w-[16px] h-[23px] group-hover:text-navyBlue' />
            </Button>
            <Button
              className='group min-w-[128px]'
              color='light'
              variant='outlined'
              onClick={() => handleCopyToClipboard()}
            >
              Copy url
              <FaLink className='mx-2 my-0 lg:my-2 w-[16px] h-[23px] group-hover:text-alizarin' />
            </Button>
          </>
        )
      },
      [SocialBlockType.WhyNativeTrees]: {
        heading: 'Why native trees?',
        description:
          'From enhancing biodiversity to growing healthier communities – find out 20 reasons we love native trees.',
        image: InformationBlockImage.Individual,
        buttons: (
          <ButtonLink
            className='group min-w-[128px]'
            color='light'
            variant='outlined'
            href={whyNativeTreesPath}
          >
            Learn more
          </ButtonLink>
        )
      }
    }

  const renderSocialBlock = (type: SocialBlockType) => {
    const block = informationBlocks[type]
    return (
      <InformationBlock
        key={`informationblock-${block.image}`}
        heading={block.heading}
        description={block.description}
        image={block.image}
        buttons={block.buttons}
      />
    )
  }

  return {
    renderSocialBlock,
    handleShareFacebook,
    handleShareLinkedIn,
    handleCopyToClipboard
  }
}

export default useSocialBlocks
